<template>
  <div class="px-4 flex items-center absolute w-full h-full top-0 left-0 bg-black/50">
    <div
      class="max-w-lg mx-auto text-center bg-[#F9FAFC] px-4 sm:px-8 py-10 rounded-xl shadow w-full"
    >
      <h1 class="text-black text-2xl md:text-4xl font-bold">
        Processing <br />
        Your Submission
      </h1>
      <p class="mt-2">
        <span>
          We're uploading your files and submitting your form. <br />
          This may take a moment. <br/> Hang tight! 🚀
        </span>
      </p>

      <div class="flex flex-col gap-y-10 mt-8">
        <!-- Show individual progress bars for each file during upload -->
        <template v-if="props.phase === 'upload'">
          <!-- Default view for MusicProducer -->
          <template v-if="props.role === 'MusicProducer'">
            <div v-for="(file, index) in props.files" :key="index">
              <div class="flex flex-row gap-x-4 items-center mb-2">
                <svg
                  viewBox="0 0 51 51"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-10 h-10"
                >
                  <g clip-path="url(#clip0_4197_6477)">
                    <path
                      d="M0.333984 25.5C0.333984 18.8696 2.96791 12.5107 7.65632 7.82233C12.3447 3.13392 18.7036 0.5 25.334 0.5C31.9644 0.5 38.3232 3.13392 43.0117 7.82233C47.7001 12.5107 50.334 18.8696 50.334 25.5C50.334 32.1304 47.7001 38.4893 43.0117 43.1777C38.3232 47.8661 31.9644 50.5 25.334 50.5C18.7036 50.5 12.3447 47.8661 7.65632 43.1777C2.96791 38.4893 0.333984 32.1304 0.333984 25.5ZM25.334 28.625C24.5052 28.625 23.7103 28.2958 23.1243 27.7097C22.5382 27.1237 22.209 26.3288 22.209 25.5C22.209 24.6712 22.5382 23.8763 23.1243 23.2903C23.7103 22.7042 24.5052 22.375 25.334 22.375C26.1628 22.375 26.9576 22.7042 27.5437 23.2903C28.1297 23.8763 28.459 24.6712 28.459 25.5C28.459 26.3288 28.1297 27.1237 27.5437 27.7097C26.9576 28.2958 26.1628 28.625 25.334 28.625ZM15.959 25.5C15.959 27.9864 16.9467 30.371 18.7049 32.1291C20.463 33.8873 22.8476 34.875 25.334 34.875C27.8204 34.875 30.205 33.8873 31.9631 32.1291C33.7213 30.371 34.709 27.9864 34.709 25.5C34.709 23.0136 33.7213 20.629 31.9631 18.8709C30.205 17.1127 27.8204 16.125 25.334 16.125C22.8476 16.125 20.463 17.1127 18.7049 18.8709C16.9467 20.629 15.959 23.0136 15.959 25.5ZM9.70898 23.9375C9.70898 20.5195 11.418 16.9941 14.123 14.2891C16.8281 11.584 20.3535 9.875 23.7715 9.875C24.6309 9.875 25.334 9.17188 25.334 8.3125C25.334 7.45312 24.6309 6.75 23.7715 6.75C19.3379 6.75 15.0605 8.92773 11.9062 12.0723C8.75195 15.2168 6.58398 19.5039 6.58398 23.9375C6.58398 24.7969 7.28711 25.5 8.14648 25.5C9.00586 25.5 9.70898 24.7969 9.70898 23.9375Z"
                      fill="#EEC45C"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_4197_6477">
                      <rect
                        width="50"
                        height="50"
                        fill="white"
                        transform="translate(0.333984 0.5)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <div class="flex flex-col justify-start items-start">
                  <span class="text text-black font-medium text-base">{{
                    file.name
                  }}</span>
                  <span class="text text-gray-500 text-sm">{{
                    formatFileSize(file.dim)
                  }}</span>
                </div>
              </div>
              <div class="flex flex-row gap-x-2 items-center">
                <div class="w-full bg-gray-200 rounded-full h-3">
                  <div
                    class="bg-green-500 h-3 rounded-full transition-all duration-300"
                    :style="`width: ${file.progress}%`"
                  ></div>
                </div>
                <span class="text-sm text-gray-600">{{ file.progress }}%</span>
              </div>
            </div>
          </template>

          <!-- Special view for SoundEngineer with Before/After sections -->
          <template v-else-if="props.role === 'SoundEngineer'">
            <div class="flex flex-col gap-y-10">
              <div>
                <h3 class="text-lg font-semibold text-left mb-4">Before Mix</h3>
                <div class="flex flex-col gap-y-4">
                  <div v-for="file in beforeFiles" :key="file.id">
                    <div class="flex flex-row gap-x-4 items-center mb-2">
                      <svg
                        viewBox="0 0 51 51"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-10 h-10"
                      >
                        <g clip-path="url(#clip0_4197_6477)">
                          <path
                            d="M0.333984 25.5C0.333984 18.8696 2.96791 12.5107 7.65632 7.82233C12.3447 3.13392 18.7036 0.5 25.334 0.5C31.9644 0.5 38.3232 3.13392 43.0117 7.82233C47.7001 12.5107 50.334 18.8696 50.334 25.5C50.334 32.1304 47.7001 38.4893 43.0117 43.1777C38.3232 47.8661 31.9644 50.5 25.334 50.5C18.7036 50.5 12.3447 47.8661 7.65632 43.1777C2.96791 38.4893 0.333984 32.1304 0.333984 25.5ZM25.334 28.625C24.5052 28.625 23.7103 28.2958 23.1243 27.7097C22.5382 27.1237 22.209 26.3288 22.209 25.5C22.209 24.6712 22.5382 23.8763 23.1243 23.2903C23.7103 22.7042 24.5052 22.375 25.334 22.375C26.1628 22.375 26.9576 22.7042 27.5437 23.2903C28.1297 23.8763 28.459 24.6712 28.459 25.5C28.459 26.3288 28.1297 27.1237 27.5437 27.7097C26.9576 28.2958 26.1628 28.625 25.334 28.625ZM15.959 25.5C15.959 27.9864 16.9467 30.371 18.7049 32.1291C20.463 33.8873 22.8476 34.875 25.334 34.875C27.8204 34.875 30.205 33.8873 31.9631 32.1291C33.7213 30.371 34.709 27.9864 34.709 25.5C34.709 23.0136 33.7213 20.629 31.9631 18.8709C30.205 17.1127 27.8204 16.125 25.334 16.125C22.8476 16.125 20.463 17.1127 18.7049 18.8709C16.9467 20.629 15.959 23.0136 15.959 25.5ZM9.70898 23.9375C9.70898 20.5195 11.418 16.9941 14.123 14.2891C16.8281 11.584 20.3535 9.875 23.7715 9.875C24.6309 9.875 25.334 9.17188 25.334 8.3125C25.334 7.45312 24.6309 6.75 23.7715 6.75C19.3379 6.75 15.0605 8.92773 11.9062 12.0723C8.75195 15.2168 6.58398 19.5039 6.58398 23.9375C6.58398 24.7969 7.28711 25.5 8.14648 25.5C9.00586 25.5 9.70898 24.7969 9.70898 23.9375Z"
                            fill="#EEC45C"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_4197_6477">
                            <rect
                              width="50"
                              height="50"
                              fill="white"
                              transform="translate(0.333984 0.5)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                      <div class="flex flex-col justify-start items-start">
                        <span class="text text-black font-medium text-base">{{
                          file.name
                        }}</span>
                        <span class="text text-gray-500 text-sm">{{
                          formatFileSize(file.dim)
                        }}</span>
                      </div>
                    </div>
                    <div class="flex flex-row gap-x-2 items-center">
                      <div class="w-full bg-gray-200 rounded-full h-3">
                        <div
                          class="bg-green-500 h-3 rounded-full transition-all duration-300"
                          :style="`width: ${file.progress}%`"
                        ></div>
                      </div>
                      <span class="text-sm text-gray-600">{{ file.progress }}%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 class="text-lg font-semibold text-left mb-4">After Mix</h3>
                <div class="flex flex-col gap-y-4">
                  <div v-for="file in afterFiles" :key="file.id">
                    <div class="flex flex-row gap-x-4 items-center mb-2">
                      <svg
                        viewBox="0 0 51 51"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-10 h-10"
                      >
                        <g clip-path="url(#clip0_4197_6477)">
                          <path
                            d="M0.333984 25.5C0.333984 18.8696 2.96791 12.5107 7.65632 7.82233C12.3447 3.13392 18.7036 0.5 25.334 0.5C31.9644 0.5 38.3232 3.13392 43.0117 7.82233C47.7001 12.5107 50.334 18.8696 50.334 25.5C50.334 32.1304 47.7001 38.4893 43.0117 43.1777C38.3232 47.8661 31.9644 50.5 25.334 50.5C18.7036 50.5 12.3447 47.8661 7.65632 43.1777C2.96791 38.4893 0.333984 32.1304 0.333984 25.5ZM25.334 28.625C24.5052 28.625 23.7103 28.2958 23.1243 27.7097C22.5382 27.1237 22.209 26.3288 22.209 25.5C22.209 24.6712 22.5382 23.8763 23.1243 23.2903C23.7103 22.7042 24.5052 22.375 25.334 22.375C26.1628 22.375 26.9576 22.7042 27.5437 23.2903C28.1297 23.8763 28.459 24.6712 28.459 25.5C28.459 26.3288 28.1297 27.1237 27.5437 27.7097C26.9576 28.2958 26.1628 28.625 25.334 28.625ZM15.959 25.5C15.959 27.9864 16.9467 30.371 18.7049 32.1291C20.463 33.8873 22.8476 34.875 25.334 34.875C27.8204 34.875 30.205 33.8873 31.9631 32.1291C33.7213 30.371 34.709 27.9864 34.709 25.5C34.709 23.0136 33.7213 20.629 31.9631 18.8709C30.205 17.1127 27.8204 16.125 25.334 16.125C22.8476 16.125 20.463 17.1127 18.7049 18.8709C16.9467 20.629 15.959 23.0136 15.959 25.5ZM9.70898 23.9375C9.70898 20.5195 11.418 16.9941 14.123 14.2891C16.8281 11.584 20.3535 9.875 23.7715 9.875C24.6309 9.875 25.334 9.17188 25.334 8.3125C25.334 7.45312 24.6309 6.75 23.7715 6.75C19.3379 6.75 15.0605 8.92773 11.9062 12.0723C8.75195 15.2168 6.58398 19.5039 6.58398 23.9375C6.58398 24.7969 7.28711 25.5 8.14648 25.5C9.00586 25.5 9.70898 24.7969 9.70898 23.9375Z"
                            fill="#EEC45C"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_4197_6477">
                            <rect
                              width="50"
                              height="50"
                              fill="white"
                              transform="translate(0.333984 0.5)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                      <div class="flex flex-col justify-start items-start">
                        <span class="text text-black font-medium text-base">{{
                          file.name
                        }}</span>
                        <span class="text text-gray-500 text-sm">{{
                          formatFileSize(file.dim)
                        }}</span>
                      </div>
                    </div>
                    <div class="flex flex-row gap-x-2 items-center">
                      <div class="w-full bg-gray-200 rounded-full h-3">
                        <div
                          class="bg-green-500 h-3 rounded-full transition-all duration-300"
                          :style="`width: ${file.progress}%`"
                        ></div>
                      </div>
                      <span class="text-sm text-gray-600">{{ file.progress }}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Special view for GraphicDesigner with image icon -->
          <template v-else-if="props.role === 'GraphicDesigner'">
            <div v-for="(file, index) in props.files" :key="index">
              <div class="flex flex-row gap-x-4 items-center mb-2 text-principal-500">
                <!-- Image Icon SVG for GraphicDesigner -->
                <svg
                  class="h-7 w-7 sm:h-10 sm:w-10"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 512 512"
                >
                  <!--!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.-->
                  <path
                    d="M448 80c8.8 0 16 7.2 16 16l0 319.8-5-6.5-136-176c-4.5-5.9-11.6-9.3-19-9.3s-14.4 3.4-19 9.3L202 340.7l-30.5-42.7C167 291.7 159.8 288 152 288s-15 3.7-19.5 10.1l-80 112L48 416.3l0-.3L48 96c0-8.8 7.2-16 16-16l384 0zM64 32C28.7 32 0 60.7 0 96L0 416c0 35.3 28.7 64 64 64l384 0c35.3 0 64-28.7 64-64l0-320c0-35.3-28.7-64-64-64L64 32zm80 192a48 48 0 1 0 0-96 48 48 0 1 0 0 96z"
                  />
                </svg>
                <div class="flex flex-col justify-start items-start">
                  <span class="text text-black font-medium text-base">{{
                    file.name
                  }}</span>
                  <span class="text text-gray-500 text-sm">{{
                    formatFileSize(file.dim)
                  }}</span>
                </div>
              </div>
              <div class="flex flex-row gap-x-2 items-center">
                <div class="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    class="bg-green-500 h-3 transition-all duration-300"
                    :style="`width: ${file.progress}%`"
                  ></div>
                </div>
                <span class="text-sm text-gray-600">{{ file.progress }}%</span>
              </div>
            </div>
          </template>

          <!-- Default view for any other role -->
          <template v-else>
            <div v-for="(file, index) in props.files" :key="index">
              <div class="flex flex-row gap-x-4 items-center mb-2">
                <svg
                  viewBox="0 0 51 51"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-10 h-10"
                >
                  <g clip-path="url(#clip0_4197_6477)">
                    <path
                      d="M0.333984 25.5C0.333984 18.8696 2.96791 12.5107 7.65632 7.82233C12.3447 3.13392 18.7036 0.5 25.334 0.5C31.9644 0.5 38.3232 3.13392 43.0117 7.82233C47.7001 12.5107 50.334 18.8696 50.334 25.5C50.334 32.1304 47.7001 38.4893 43.0117 43.1777C38.3232 47.8661 31.9644 50.5 25.334 50.5C18.7036 50.5 12.3447 47.8661 7.65632 43.1777C2.96791 38.4893 0.333984 32.1304 0.333984 25.5ZM25.334 28.625C24.5052 28.625 23.7103 28.2958 23.1243 27.7097C22.5382 27.1237 22.209 26.3288 22.209 25.5C22.209 24.6712 22.5382 23.8763 23.1243 23.2903C23.7103 22.7042 24.5052 22.375 25.334 22.375C26.1628 22.375 26.9576 22.7042 27.5437 23.2903C28.1297 23.8763 28.459 24.6712 28.459 25.5C28.459 26.3288 28.1297 27.1237 27.5437 27.7097C26.9576 28.2958 26.1628 28.625 25.334 28.625ZM15.959 25.5C15.959 27.9864 16.9467 30.371 18.7049 32.1291C20.463 33.8873 22.8476 34.875 25.334 34.875C27.8204 34.875 30.205 33.8873 31.9631 32.1291C33.7213 30.371 34.709 27.9864 34.709 25.5C34.709 23.0136 33.7213 20.629 31.9631 18.8709C30.205 17.1127 27.8204 16.125 25.334 16.125C22.8476 16.125 20.463 17.1127 18.7049 18.8709C16.9467 20.629 15.959 23.0136 15.959 25.5ZM9.70898 23.9375C9.70898 20.5195 11.418 16.9941 14.123 14.2891C16.8281 11.584 20.3535 9.875 23.7715 9.875C24.6309 9.875 25.334 9.17188 25.334 8.3125C25.334 7.45312 24.6309 6.75 23.7715 6.75C19.3379 6.75 15.0605 8.92773 11.9062 12.0723C8.75195 15.2168 6.58398 19.5039 6.58398 23.9375C6.58398 24.7969 7.28711 25.5 8.14648 25.5C9.00586 25.5 9.70898 24.7969 9.70898 23.9375Z"
                      fill="#EEC45C"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_4197_6477">
                      <rect
                        width="50"
                        height="50"
                        fill="white"
                        transform="translate(0.333984 0.5)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <div class="flex flex-col justify-start items-start">
                  <span class="text text-black font-medium text-base">{{
                    file.name
                  }}</span>
                  <span class="text text-gray-500 text-sm">{{
                    formatFileSize(file.dim)
                  }}</span>
                </div>
              </div>
              <div class="flex flex-row gap-x-2 items-center">
                <div class="w-full bg-gray-200 rounded-full h-3">
                  <div
                    class="bg-green-500 h-3 rounded-full transition-all duration-300"
                    :style="`width: ${file.progress}%`"
                  ></div>
                </div>
                <span class="text-sm text-gray-600">{{ file.progress }}%</span>
              </div>
            </div>
          </template>
        </template>

        <!-- Show spinner when in form submission phase -->
        <template v-else>
          <div class="flex justify-center">
            <div
              class="animate-spin rounded-full h-8 w-8 border-4 border-principal-500 border-t-transparent"
            ></div>
          </div>
          <p class="text-sm text-gray-600 mt-2">Processing</p>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed } from "vue";
import { formatFileSize } from "../utils/helper";

const props = defineProps({
  isUploading: Boolean,
  files: {
    type: Array,
    default: () => [],
  },
  phase: {
    type: String,
    default: "upload",
    validator: (value) => ["upload", "submission"].includes(value),
  },
  role: {
    type: String,
    default: "MusicProducer",
    validator: (value) =>
      ["MusicProducer", "SoundEngineer", "GraphicDesigner"].includes(value),
  },
});

// For SoundEngineer, separate files into before and after categories
const beforeFiles = computed(() => {
  if (props.role !== "SoundEngineer") return [];
  return props.files.filter((file) => file.id === 1 || file.id === 3);
});

const afterFiles = computed(() => {
  if (props.role !== "SoundEngineer") return [];
  return props.files.filter((file) => file.id === 2 || file.id === 4);
});
</script>
